import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.scss";
import StepWizard from "react-step-wizard";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  EmergencyInputField,
  GenericButton,
  GenericModal,
  InputField,
  MaterialInputField,
  InsuranceInputField,
  ActionButtons,
  PhoneInputField,
} from "components";
import Grid from "@mui/material/Grid";
import {
  signUpSchema,
  signUpDetailsSchema,
  signUpStep1Schema,
  signUpStep2Schema,
  signUpStep3Schema,
  signUpStep4Schema,
} from "common/schemas";
import { Formik } from "formik";
import {
  ENUMS,
  GenderList,
  constants,
  countriesList,
  emergencyRelationshipList,
  BASE_CURRENCY,
  MEMBERSHIP_PRICE,
  CORPORATE_PRICE,
} from "common";
import { getPlusPrice, getAgeFromDob, PLUS_CURRENCIES } from "common/insurancePricing";
import { toIso2, needsStateCode } from "common/countryIso";
import { images } from "common/images";
import PasswordStrengthBar from "react-password-strength-bar";
import { Link, useNavigate, useParams } from "react-router-dom";
import { auth, performSignUpThroughFirebase, savePolicy, insuranceApi } from "services";
import Autocomplete from "react-google-autocomplete";
import { RibbonContainer, Ribbon } from "react-ribbons";
import {
  checkIfEmailExists,
  getUserDetails,
  getUserByEmail,
  performLoginThroughFirebase,
  saveUserData,
} from "services/firebaseServices";
import {
  generateAndSendOtpCode,
  verifyOtpCode,
} from "utilities/otpService";
import { sendInsuranceConfirmation } from "utilities/resendEmail";
import {
  setTempUserDetail,
  getTempUserDetail,
  setToken,
  setProfile,
  setSecondaryProfile,
} from "utilities/localStorage";
import { routePaths } from "routes";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "pages/home";
import { ApplicationContext } from "shared";
import { nationality } from "common/nationality";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { OrganDonarOptions } from "common/constants";
import { MedicalCard } from "../../components/medical-card";
import { Navigation } from "components/navigation";
import { isEighteenOrOlder } from "common/isEighteenOrOlder";
import { civilStatus } from "common/civilstatus";
import { onAuthStateChanged } from "firebase/auth";
import { CircularProgress } from "@mui/material";
import { Checkbox, FormControlLabel } from "@mui/material";
import _ from "lodash";
import { fireAwinConversion } from "utilities/awinTracking";

const {
  formHeadings: { step1, step2, step3, step4, step5, step6, step7 },
  generateText,
  getOurApp,
  pleaseFilloutPersonalInfo,
  registerNewWMCAcc,
  alreadyHaveAndAcc,
  placeholders,
  fieldTypeEnums,
} = constants;

export const converDateFormate = (dateString) => {
  const originalDate = new Date(dateString);
  const formattedDate = originalDate.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return formattedDate;
};
// Format any date value into the YYYY-MM-DD a native <input type="date"> needs.
export const toInputDate = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
export const formatLabel = (text) => {
  return text?.replace(/[-_]/g, " ").toLowerCase();
};

// Insurance compliance documents + regulatory footer (kept in sync with the
// Insurance page). Links are overridable via env; the disclosures URL and the
// legal footer text are fixed by battleface / Crum & Forster.
const POLICY_URL = "/documents/wmc-travel-insurance-policy.pdf";
const DISCLOSURES_URL = "/documents/wmc-insurance-disclosures.pdf";
const LEGAL_FOOTER =
  "Travel medical insurance plan administered by battleface Underwriting Services SRL. Insurance benefits are underwritten by Crum & Forster, SPC. C&F and Crum & Forster are registered trademarks of Crum & Forster, SPC.";
const INSURANCE_CONDITIONS_NOTE =
  "Annual multi-trip cover, up to 30 days per trip. Excludes domestic trips. Pre-existing medical conditions excluded. Cover limits, deductible and exclusions are set out in full in your policy wording.";
const INSURANCE_COOLING_OFF_NOTE =
  "You have the right to cancel within 14 days of receiving these documents. Any refund, and whether it applies once you've travelled or made a claim, is governed by the terms in your policy wording.";
const INSURANCE_TRUST_NOTE =
  "By purchasing this insurance provided by Crum & Forster SPC, under the jurisdiction of the Cayman Islands, you become a member of the Fairmont Specialty Trust.";
const INSURANCE_PLAN_SUMMARY_NOTE =
  "Notice: For further information on this Plan, visit www.battleface.com. Please keep this summary as a brief description of the important features of the plan. It is not a contract of insurance. This plan includes both insurance and non-insurance benefits. The terms and conditions of coverage are set forth in the Plan issued to World Medical Card. For a detailed plan description, exclusions, and limitations please view the plan on file with World Medical Card. The Policy contains a complete description of all of the terms, conditions, and exclusions of the insurance plan as underwritten by Crum & Forster, SPC. The Policy will prevail in the event of any discrepancy between this and the Policy.";
const INSURANCE_PRIVACY_NOTE =
  "Please note that sensitive health and other information that you provide may be used by us, our representatives, the insurers and industry governing bodies and regulators to process your insurance, handle claims and prevent fraud. This may involve transferring information to other countries (some of which may have limited, or no data protection laws). We have taken steps to ensure your information is held securely. Where sensitive personal information relates to anyone other than you, you must obtain the explicit consent of the person to whom the information relates both to the disclosure of such information to us and its use as set out above. Information we hold will not be shared with third parties for marketing purposes. You have the right to access your personal records. We know that your privacy is important to you and we strive to protect the confidentiality of your non-public personal information. We do not disclose any non-public personal information about our insureds or former insureds to anyone, except as permitted or required by law. We maintain appropriate physical, electronic and procedural safeguards to ensure the security of your non-public personal information. You may obtain a detailed copy of our privacy policy by calling us on +32 284 42 256 or by visiting us at https://www.battleface.com.";
const INSURANCE_COMPLAINTS_NOTE =
  "In the event that you remain dissatisfied and wish to make a complaint you can do so to the Complaints team at complaints@battleface.com.";

const SignUp = () => {
  // debugger;
  let navigate = useNavigate();
  const { company } = useParams();
  console.log("formatLabel(company)", formatLabel(company));
  const isCompanyLogin = !!company;
  const [btnLoader, setBtnLoader] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderNowBtnLoader, setOrderNowBtnLoader] = useState(false);
  const [password, setPassword] = useState("");
  const { wmcLogo, mobileLogo, appStoreLogo, playStoreLogo } = images;
  // Insurance features (Plus plans + the extra name/DOB step) only when the flag is on.
  const insuranceEnabled = process.env.REACT_APP_INSURANCE_ENABLED === "true";
  const [activeStep, setActiveStep] = useState(0);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  // Post-payment insurance confirmation (shown before we sign the user in).
  const [insConfirm, setInsConfirm] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [apiResponse, setApiResponse] = useState({
    type: ENUMS.MODAL_TYPES.ERROR,
    title: "",
    body: "",
  });
  const [userDetails, setUserDetails] = useState({
    avatar: "",
    firstName: "",
    lastName: "",
    email: "",
    birthDate: "",
    gender: "",
    cprNumber: "",
    nationality: "",
    phone: "",
    ssn: "",
    donarNumber: "",
    address: "",
    insuranceType: "Choose",
    insuranceCompany: "",
    policyNumber: "",
    emergencyNumber: "+1",
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",
    password: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    organDonation: "",
    cardOrderStatus: "",
    cardOrderDate: "",
    isActiveSubscription: true,
    isForcedRefreshToken: false,
    memberSince: formatDate(new Date()),
    cardReOrderStatus: "",
    cardReOrderDate: "",
    homephone: "",
    confirmPassword: "",
    company: isCompanyLogin ? formatLabel(company) : "Self",
    relationShip: "",
    price: typeof window !== 'undefined' && window.location.hostname.includes('.co.uk') ? 24 : MEMBERSHIP_PRICE,
    currency: typeof window !== 'undefined' && window.location.hostname.includes('.co.uk') ? 'gbp' : BASE_CURRENCY.toLowerCase(),
    corporatePrice: typeof window !== 'undefined' && window.location.hostname.includes('.co.uk') ? 26 : CORPORATE_PRICE,
  });
  const isObjectEmpty = (objectName) => {
    return Object.keys(objectName).length === 0;
  };
  const transformUserData = (flatUserData) => {
    let insuranceExtra = extractItemBeforeUnderscore(insuranceFields) ?? [];
    let emergencyExtra = extractItemBeforeUnderscore(emrgencyExtFields) ?? [];
    const {
      insuranceType,
      insuranceCompany,
      policyNumber,
      emergencyNumber,
      emergencyName,
      emergencyPhone,
      emergencyRelation,
      address,
      city,
      state,
      zipCode,
      country,
      ...rest
    } = flatUserData;

    // Handle insurance data (optional fields)
    let tempInsurance = {
      type: insuranceType || "",
      company: insuranceCompany || "",
      policy: policyNumber || "",
      phone: "",
    };

    // Handle emergency data (optional during signup, will be filled via post-login modal)
    let tempEmergency = {
      name: emergencyName || "",
      phone: emergencyPhone || "",
      relationship: emergencyRelation || "",
    };

    const updatedInsurance = insuranceType
      ? [...insuranceExtra, tempInsurance]
      : [];

    // Build user details with optional fields
    const userDetails = {
      ...rest,
      insurance: updatedInsurance.length > 0 ? updatedInsurance : [],
      emergencyContact: localStorage.getItem("emergencyContacts")
        ? JSON.parse(localStorage.getItem("emergencyContacts"))
        : emergencyName
          ? [tempEmergency]
          : [],
      postalAddress: address
        ? [
            {
              city: city || "",
              state: state || "",
              info: address || "",
              zipCode: zipCode || "",
              country: country || "",
            },
          ]
        : [],
    };
    return userDetails;
  };

  useEffect(() => {
    if (searchParams?.get("session_id")) {
      const sessionId = searchParams?.get("session_id");
      const payment = searchParams.get("payment");

      // Only save user if Stripe confirms the payment was actually completed
      if (payment && payment !== '{CHECKOUT_SESSION_ID}') {
        // Returning from Stripe — show a full-screen loading state while we
        // verify the payment, save the account and sign the user in.
        setPaymentProcessing(true);
        const brand = window.location.hostname.includes('.co.uk') ? 'uk' : 'io';
        fetch('/api/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: payment, brand }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.paid === true) {
              saveDataToFirebase(sessionId, payment);
            } else {
              console.warn('[Sign-up] Payment not confirmed — not saving user. Status:', data);
              setPaymentProcessing(false);
            }
          })
          .catch(err => {
            console.error('[Sign-up] Payment verification failed:', err);
            setPaymentProcessing(false);
          });
      }
    }
  }, [searchParams]);

  function extractItemBeforeUnderscore(data) {
    return data.map((item) => {
      const result = {};

      for (const key in item) {
        if (item.hasOwnProperty(key)) {
          const [itemName, index] = key.split("_");
          result[itemName] = item[key];
        }
      }

      return result;
    });
  }

  const handleStepChange = (e) => {
    setActiveStep(e.activeStep - 1);
  };

  // IP-based pricing — runs once on mount, skips .co.uk (handled by hostname)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname.includes('.co.uk')) return;
    const NORDIC_PRICES = {
      NO: { price: 279.95, currency: 'nok', corporatePrice: 349.95 },
      SE: { price: 219.95, currency: 'sek', corporatePrice: 295.95 },
      DK: { price: 149.95, currency: 'dkk', corporatePrice: 199.95 },
      US: { price: 19.95, currency: 'usd', corporatePrice: 24.95 },
    };
    const detectCountry = async () => {
      let countryCode = null;
      try {
        // api.country.is is minimal and not blocked by privacy tools
        const res = await fetch('https://api.country.is/');
        const data = await res.json();
        countryCode = data?.country;
      } catch {
        // fallback to ipwho.is
        try {
          const res2 = await fetch('https://ipwho.is/');
          const data2 = await res2.json();
          countryCode = data2?.country_code;
        } catch { /* Silently fail — default EUR price remains unchanged */ }
      }
      if (countryCode && NORDIC_PRICES[countryCode]) {
        setUserDetails((prev) => ({
          ...prev,
          price: NORDIC_PRICES[countryCode].price,
          currency: NORDIC_PRICES[countryCode].currency,
          corporatePrice: NORDIC_PRICES[countryCode].corporatePrice,
        }));
      }
    };
    detectCountry();
  }, []);

  const generatePassword = () => {
    let charset = "";
    let newPassword = "";
    charset += "!@#$%^&*()";
    charset += "0123456789";
    charset += "abcdefghijklmnopqrstuvwxyz";
    charset += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    for (let i = 0; i < 12; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    setPassword(newPassword);
  };

  const convertArrayToString = (data) => {
    const joined = data?.join();
    let newAddress = joined?.replace(/,/g, " ");
    return newAddress;
  };

  const AddressSeprateHandler = (data, setFieldValue, values) => {
    let address = [];
    let city = [];
    let state = [];
    let postal = [];
    let country = [];
    data.map((dataItem) => {
      dataItem.types.map((type) => {
        if (type === fieldTypeEnums.streetNumber) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.route) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.naibhour) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.locality) {
          city.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.admin) {
          state.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.country) {
          country.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.code) {
          postal.push(dataItem.long_name);
        }
      });
    });
    setFieldValue(userDetails.address, values);
    setFieldValue(userDetails.city, convertArrayToString(city));
    setFieldValue(userDetails.state, convertArrayToString(state));
    setFieldValue(userDetails.zipCode, convertArrayToString(postal));
    setFieldValue(userDetails.country, convertArrayToString(country));
    setUserDetails((data) => ({
      ...data,
      address: values,
      city: convertArrayToString(city),
      state: convertArrayToString(state),
      zipCode: convertArrayToString(postal),
      country: convertArrayToString(country),
    }));
  };

  function Step1(props) {
    return (
      <Formik
        initialValues={{
          email: userDetails.email,
          password: password || userDetails.password,
          confirmPassword: password || userDetails.confirmPassword || "",
          phone: userDetails.phone,
          marketingConsent: false,
        }}
        validateOnMount={true}
        onSubmit={(values) => {
          setUserDetails((data) => ({
            ...data,
            phone: values.phone,
            password: values.password,
            confirmPassword: values.confirmPassword,
          }));
          checkEmail(values, props);
        }}
        validationSchema={signUpSchema}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          values,
          setFieldValue,
          setFieldError,
        }) => {
          const handleEmailBlur = async (e) => {
            handleBlur(e);
            const emailValue = e.target.value;
            if (emailValue && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
              // Query Firestore directly — avoids Firebase Email Enumeration Protection
              // which silently breaks fetchSignInMethodsForEmail
              const existingDoc = await getUserByEmail(emailValue);
              if (existingDoc) {
                setFieldError("email", "This email address is already registered.");
              }
            }
          };

          return (
          <div className="sign-up-step-cont">
            <div>
              <div className="sign-up-form-heading">
                <span>{"Sign Up"}</span>
              </div>
              <Grid container spacing={1.5}>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <MaterialInputField
                    // icon={<Email/>}
                    name={"email"}
                    id={"email"}
                    placeholder={"Email"}
                    label={"Email"}
                    error={touched.email && errors.email}
                    handleChange={handleChange}
                    handleBlur={handleEmailBlur}
                    value={values.email}
                  />
                </Grid>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <PhoneInputField
                    name={"phone"}
                    id={"phone"}
                    value={values.phone}
                    handleChange={handleChange("phone")}
                    handleBlur={handleBlur("phone")}
                    specialLabel={"Phone Number"}
                    error={touched.phone && errors.phone}
                  />
                </Grid>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <MaterialInputField
                    customStyle={"pass-input-custom-style"}
                    id={"password"}
                    name={"password"}
                    placeholder={"Password"}
                    label={"Password"}
                    error={touched.password && errors.password}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    value={values.password}
                    isPassword
                  />
                  {values.password != "" && (
                    <div className="pw-strength">
                      <PasswordStrengthBar
                        password={values.password || password}
                      />
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <MaterialInputField
                    customStyle={"pass-input-custom-style"}
                    id={"confirmPassword"}
                    name={"confirmPassword"}
                    placeholder={"Confirm Password"}
                    label={"Confirm Password"}
                    error={touched.confirmPassword && errors.confirmPassword}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    value={values.confirmPassword}
                    isPassword
                    isSearch
                  />
                  {values.confirmPassword != "" && (
                    <div className="pw-strength">
                      <PasswordStrengthBar
                        password={values.confirmPassword || password}
                      />
                    </div>
                  )}
                </Grid>
                {/* <div className="password-generator-cont">
                  <div className="generate-text-btn-cont">
                    <div>
                      <button
                        className="buttonStyle"
                        onClick={() => {
                          setUserDetails((data) => ({
                            ...data,
                            email: values.email,
                          }));
                          generatePassword();
                        }}
                      >
                        <span className="generate-text">{generateText}</span>
                      </button>
                    </div>
                  </div>
                </div> */}
                {/* Marketing Consent Checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.marketingConsent}
                        onChange={(e) =>
                          setFieldValue("marketingConsent", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="I agree to receive marketing emails and updates."
                  />
                </Grid>
                <div className="error-input-container">
                  {touched.marketingConsent && errors.marketingConsent ? (
                    <p className="form-error">{errors.marketingConsent}</p>
                  ) : null}
                </div>
              </Grid>
            </div>
            <ActionButtons
              setUserDetails={setUserDetails}
              userDetails={userDetails}
              handleSubmit={handleSubmit}
              activeStep={activeStep}
              {...props}
            />
          </div>
          );
        }}
      </Formik>
    );
  }

  function Step2(props) {
    return (
      <Formik
        initialValues={{
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          dob: userDetails.birthDate || null,
          phone: userDetails.phone,
          nationality: userDetails.nationality,
          gender: userDetails.gender,
          socialNum: userDetails.ssn,
          organDonar: userDetails.organDonation,
          homephone: userDetails.homephone,
          relationShip: userDetails.relationShip,
        }}
        validateOnMount={true}
        onSubmit={(values) => {
          let date = converDateFormate(values.dob);
          console.log("date", date);
          setUserDetails((data) => ({
            ...data,
            firstName: values.firstName,
            lastName: values.lastName,
            birthDate: date,
            phone: values.phone,
            nationality: values.nationality,
            gender: values.gender,
            ssn: values.socialNum,
            organDonation: values.organDonar,
            homephone: values.homephone,
            relationShip: values.relationShip,
          }));
          props.nextStep();
        }}
        validationSchema={signUpStep1Schema}
        enableReinitialize={true}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          values,
          setFieldValue,
        }) => (
          <div style={{ padding: 10 }}>
            <div>
              <div className="sign-up-form-heading">
                <span>{"Personal Information"}</span>
              </div>
              <Grid container spacing={0.7}>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb">
                    <MaterialInputField
                      name={"firstName"}
                      id={"firstName"}
                      error={touched.firstName && errors.firstName}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label={"First Name"}
                      value={values.firstName}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <MaterialInputField
                    name={"lastName"}
                    id={"lastName"}
                    error={touched.lastName && errors.lastName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    label={"Last Name"}
                    value={values.lastName}
                  />
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="full-width-datepicker">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileDatePicker
                        label="DOB *"
                        value={values.dob ? dayjs(values.dob) : null}
                        onChange={(newValue) => {
                          setFieldValue("dob", newValue ? newValue.toDate() : null);
                        }}
                        maxDate={dayjs()}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              boxShadow: "none",
                              backgroundColor: "#fafafa",
                              borderRadius: "10px",
                              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                              "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                              "& .MuiInputBase-input": { fontSize: "16px" },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <div className="error-input-container">
                      {errors.dob && touched.dob ? (
                        <p className="form-error">{errors.dob}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb">
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                          Gender
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: 0 },
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={values.gender}
                          label="gender"
                          onChange={handleChange("gender")}
                        >
                          {GenderList.map(({ value, label }) => {
                            return <MenuItem value={value}>{label}</MenuItem>;
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className="error-input-container">
                      {errors.gender && touched.gender ? (
                        <p className="form-error">{errors.gender}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                          Nationality
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: 0 },
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={values.nationality}
                          label="Select a nationality"
                          onChange={handleChange("nationality")}
                        >
                          {nationality.map((nationality, index) => (
                            <MenuItem key={index} value={nationality}>
                              {nationality}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className="error-input-container">
                      {errors.nationality && touched.nationality ? (
                        <p className="form-error">{errors.nationality}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                          Civil Status
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: 0 },
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={values.relationShip}
                          label="Civil Status"
                          onChange={handleChange("relationShip")}
                        >
                          {civilStatus.map((relationShip, index) => (
                            <MenuItem key={index} value={relationShip}>
                              {relationShip}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb">
                    <MaterialInputField
                      name={"socialNum"}
                      id={"socialNum"}
                      error={touched.socialNum && errors.socialNum}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label={"National ID number"}
                      value={values.socialNum}
                    />
                  </div>
                </Grid>
                {/* PHONE FIELD MOVED TO STEP1 - COMMENTED OUT */}
                {/* <Grid item xs={12} xl={6} lg={6} md={6}>
                  <PhoneInputField
                    name={"phone"}
                    id={"phone"}
                    value={values.phone}
                    handleChange={handleChange("phone")}
                    handleBlur={handleBlur("phone")}
                    specialLabel={"Phone Number"}
                    // placeholder={"Phone Number"}
                    error={touched.phone && errors.phone}
                  />
                </Grid> */}
                {/* <Grid item xs={12} xl={6} lg={6} md={6}>
                  <PhoneInputField
                    name={"homephone"}
                    id={"homephone"}
                    value={values.homephone}
                    handleChange={handleChange("homephone")}
                    handleBlur={handleBlur("homephone")}
                    placeholder={"Home Phone"}
                    error={touched.homephone && errors.homephone}
                  />
                </Grid> */}
                <label style={{ paddingLeft: 10, paddingTop: 10 }}>
                  Would you like to be an organ donor? (optional)
                </label>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <Box sx={{ minWidth: 120 }}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        Organ Donor
                      </InputLabel>
                      <Select
                        sx={{
                          boxShadow: "none",
                          ".MuiOutlinedInput-notchedOutline": { border: 0 },
                        }}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={values.organDonar}
                        label="Organ Donor"
                        onChange={handleChange("organDonar")}
                        placeholder="Organ Donor"
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </div>
            <ActionButtons
              setUserDetails={setUserDetails}
              userDetails={userDetails}
              handleSubmit={handleSubmit}
              activeStep={activeStep}
              {...props}
            />
          </div>
        )}
      </Formik>
    );
  }

  function Step3(props) {
    return (
      <Formik
        initialValues={{
          address: userDetails.address,
          city: userDetails.city,
          state: userDetails.state,
          zipCode: userDetails.zipCode,
          country: userDetails.country,
        }}
        validateOnMount={true}
        onSubmit={(values) => {
          setUserDetails((data) => ({
            ...data,
            address: values.address,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
            country: values.country,
          }));
          props.nextStep();
        }}
        validationSchema={signUpStep2Schema}
        enableReinitialize
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          values,
          setFieldValue,
        }) => (
          <div style={{ padding: 10 }}>
            <div>
              <div className="sign-up-form-heading">
                <span>{"Address"}</span>
              </div>
              <Grid container spacing={0.7}>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <div className="mb">
                    <Autocomplete
                      className="input-field-generic auto-complete-address"
                      apiKey={constants.googleAPIKey}
                      defaultValue={values.address}
                      onChange={handleChange("address")}
                      onBlur={handleBlur}
                      onPlaceSelected={(data) => {
                        AddressSeprateHandler(
                          data.address_components,
                          setFieldValue,
                          data.formatted_address,
                        );
                      }}
                    />
                    <div className="error-input-container">
                      {errors.address && touched.address ? (
                        <p className="form-error">{errors.address}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <InputField
                      name={"city"}
                      id={"city"}
                      error={touched.city && errors.city}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.city}
                      placeholder={placeholders.city}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <InputField
                      name={"state"}
                      id={"state"}
                      error={touched.state && errors.state}
                      handleChange={handleChange("state")}
                      handleBlur={handleBlur}
                      value={values.state}
                      placeholder={placeholders.region}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <InputField
                      name={"zipCode"}
                      id={"zipCode"}
                      error={touched.zipCode && errors.zipCode}
                      handleChange={handleChange("zipCode")}
                      handleBlur={handleBlur}
                      value={values.zipCode}
                      placeholder={placeholders.postalCode}
                      autoComplete="new-password"
                    />
                  </div>
                </Grid>

                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className="mb-1">
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                          Country
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: 0 },
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={
                            countriesList.some(
                              (item) => item.value == values.country || "",
                            )
                              ? values.country
                              : ""
                          }
                          label="Select Country"
                          onChange={handleChange("country")}
                        >
                          {countriesList.map(({ value, label }, index) => (
                            <MenuItem key={index} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className="error-input-container">
                      {errors.country && touched.country ? (
                        <p className="form-error">{errors.country}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>

                <Grid item xs={12} xl={6} lg={6} md={6}>
                  {/* <div className="mb-1">
                    <InputField
                      name={"country"}
                      id={"country"}
                      error={touched.country && errors.country}
                      handleChange={handleChange("country")}
                      handleBlur={handleBlur}
                      value={values.country}
                      placeholder={placeholders.country}
                    />
                  </div> */}

                  {/* <div className="mb-1">
                    <Select
                      sx={{
                        boxShadow: "none",
                        ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      }}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={values.country}
                      label="Country"
                      onChange={handleChange("country")}
                      placeholder="Country"
                    >
                      {countriesList.map(({label, value}) => {
                        return <MenuItem value={value}>{label}</MenuItem>
                      })}
                    </Select>
                  </div> */}
                </Grid>
              </Grid>
            </div>
            <ActionButtons
              setUserDetails={setUserDetails}
              userDetails={userDetails}
              handleSubmit={handleSubmit}
              activeStep={activeStep}
              {...props}
            />
          </div>
        )}
      </Formik>
    );
  }
  // const insuranceExtraFields = [];
  const [insuranceFields, setInsuranceFields] = useState([]);
  // function Step4(props) {
  //   const { fieldCount, setFieldCount, fields, setFields } =
  //     useContext(ApplicationContext);
  //   const handleInputChange = (fieldName, value) => {
  //     setFields({ ...fields, [fieldName]: value });
  //   };
  //   const handlePlusButtonClick = () => {
  //     let newFields = {};
  //     newFields = { ...fields, ...newFields };

  //     const newFieldIndex = fieldCount + 1;
  //     newFields[`type_${newFieldIndex}`] = "";
  //     newFields[`company_${newFieldIndex}`] = "";
  //     newFields[`policy_${newFieldIndex}`] = "";
  //     newFields[`phone_${newFieldIndex}`] = "";
  //     setFields(newFields);
  //     setFieldCount(fieldCount + 1);
  //   };
  //   const handleDeleteButtonClick = () => {
  //     const newFields = {};
  //     for (let i = 0; i < 4; i++) {
  //       const suffix = fieldCount - i;
  //       delete newFields[`type_${suffix}`];
  //       delete newFields[`company_${suffix}`];
  //       delete newFields[`policy_${suffix}`];
  //       delete newFields[`phone_${suffix}`];
  //     }
  //     setFields(newFields);
  //     setFieldCount(fieldCount - 1);
  //   };
  //   const moreFieldsHandleSubmit = (event) => {
  //     event?.preventDefault();
  //     if (fieldCount == 0) {
  //       return;
  //     } else {
  //       if (isObjectEmpty(fields) == false) {
  //         insuranceExtraFields.push(fields);
  //       }
  //     }
  //     setInsuranceFields(insuranceExtraFields);
  //   };
  //   const insuranceRef = useRef();

  //   return (
  //     <Formik
  //       initialValues={{
  //         insuranceType: userDetails.insuranceType,
  //         insuranceCompany: userDetails.insuranceCompany,
  //         policyNumber: userDetails.policyNumber,
  //         emergencyNumber: userDetails.emergencyNumber,
  //       }}
  //       validateOnMount={true}
  //       onSubmit={(values, { setFieldError }) => {
  //         const {
  //           insuranceType,
  //           insuranceCompany,
  //           policyNumber,
  //           emergencyNumber,
  //         } = values;

  //         const isAnyFieldFilled =
  //           insuranceType ||
  //           insuranceCompany ||
  //           policyNumber ||
  //           emergencyNumber;
  //         const areAllFieldsFilled =
  //           insuranceType &&
  //           insuranceCompany &&
  //           policyNumber &&
  //           emergencyNumber;

  //         if (
  //           isAnyFieldFilled &&
  //           !areAllFieldsFilled &&
  //           insuranceType !== "Choose"
  //         ) {
  //           if (!insuranceType)
  //             setFieldError("insuranceType", "InsuranceType Required");
  //           if (!insuranceCompany)
  //             setFieldError("insuranceCompany", "InsuranceCompany Required");
  //           if (!policyNumber)
  //             setFieldError("policyNumber", "PolicyNumber Required");
  //           if (!emergencyNumber)
  //             setFieldError("emergencyNumber", "EmergencyNumber Required");
  //           return;
  //         }
  //         moreFieldsHandleSubmit();
  //         setUserDetails((data) => ({
  //           ...data,
  //           insuranceType:
  //             values.insuranceType === "Choose" ? "" : values.insuranceType,
  //           insuranceCompany: values.insuranceCompany,
  //           policyNumber: values.policyNumber,
  //           emergencyNumber: values.emergencyNumber,
  //         }));
  //         props.nextStep();
  //       }}
  //       validationSchema={signUpStep3Schema}
  //       enableReinitialize
  //     >
  //       {({
  //         handleChange,
  //         handleBlur,
  //         handleSubmit,
  //         touched,
  //         errors,
  //         values,
  //         setFieldValue,
  //       }) => (
  //         <>
  //           <div className="scroll">
  //             <div>
  //               <div className="insurance-heading">
  //                 <span>{"Insurance"}</span>
  //               </div>
  //               <Grid container spacing={0.7}>
  //                 <Grid item xs={12} xl={12} lg={12} md={12}>
  //                   <div className="mb-1">
  //                     <Box sx={{ minWidth: 120 }}>
  //                       <FormControl fullWidth>
  //                         <InputLabel id="demo-simple-select-label">
  //                           Type of insurance
  //                         </InputLabel>
  //                         <Select
  //                           sx={{
  //                             boxShadow: "none",
  //                             ".MuiOutlinedInput-notchedOutline": { border: 0 },
  //                           }}
  //                           labelId="demo-simple-select-label"
  //                           id="demo-simple-select"
  //                           value={values.insuranceType}
  //                           label="insurance type"
  //                           onChange={handleChange("insuranceType")}
  //                           placeholder="Type of insurance"
  //                         >
  //                           <MenuItem value={"Choose"}>Choose</MenuItem>
  //                           <MenuItem value={"Health"}>Health</MenuItem>
  //                           <MenuItem value={"Travel"}>Travel</MenuItem>
  //                         </Select>
  //                       </FormControl>
  //                     </Box>
  //                     <div className="error-input-container">
  //                       {errors.insuranceType && touched.insuranceType ? (
  //                         <p className="form-error">{errors.insuranceType}</p>
  //                       ) : null}
  //                     </div>
  //                   </div>
  //                 </Grid>
  //                 <Grid item xs={12} xl={12} lg={12} md={12}>
  //                   <div className="mb-1">
  //                     <InputField
  //                       name={"insuranceCompany"}
  //                       id={"insuranceCompany"}
  //                       error={
  //                         touched.insuranceCompany && errors.insuranceCompany
  //                       }
  //                       handleChange={handleChange}
  //                       handleBlur={handleBlur}
  //                       fullWidth
  //                       placeholder={"Insurance Company"}
  //                       value={values.insuranceCompany}
  //                     />
  //                   </div>
  //                 </Grid>
  //                 <Grid item xs={12} xl={12} lg={12} md={12}>
  //                   <div className="mb-1">
  //                     <InputField
  //                       name={"policyNumber"}
  //                       id={"policyNumber"}
  //                       error={touched.policyNumber && errors.policyNumber}
  //                       handleChange={handleChange}
  //                       handleBlur={handleBlur}
  //                       fullWidth
  //                       placeholder={"Policy Number"}
  //                       value={values.policyNumber}
  //                     />
  //                   </div>
  //                 </Grid>
  //                 <Grid item xs={12} xl={12} lg={12} md={12}>
  //                   <div className="mb-1">
  //                     <PhoneInputField
  //                       name={"emergencyNumber"}
  //                       id={"emergencyNumber"}
  //                       error={
  //                         touched.emergencyNumber && errors.emergencyNumber
  //                       }
  //                       country={"us"}
  //                       placeholder={"Emergency phone"}
  //                       value={values.emergencyNumber}
  //                       handleBlur={handleBlur("emergencyNumber")}
  //                       handleChange={handleChange("emergencyNumber")}
  //                     />
  //                   </div>
  //                 </Grid>
  //               </Grid>
  //             </div>
  //             <InsuranceInputField
  //               fields={fields}
  //               fieldCount={fieldCount}
  //               handleInputChange={handleInputChange}
  //               handleSubmit={moreFieldsHandleSubmit}
  //               handleDeleteButtonClick={handleDeleteButtonClick}
  //               insuranceRef={insuranceRef}
  //             />
  //           </div>
  //           <div
  //             style={{
  //               alignItems: "center",
  //               flexDirection: "row",
  //               display: "flex",
  //               justifyContent: "center",
  //               marginTop: 20,
  //             }}
  //           >
  //             <div style={{ width: "30%" }}>
  //               <GenericButton
  //                 disable={fieldCount == 2}
  //                 buttonText={"+ Add Insurance"}
  //                 customStyle={"add-more-fields-btn"}
  //                 onPress={() => {
  //                   handlePlusButtonClick();
  //                   insuranceRef.current?.scrollIntoView({
  //                     behavior: "smooth",
  //                   });
  //                 }}
  //               />
  //             </div>
  //           </div>
  //           <ActionButtons
  //             setUserDetails={setUserDetails}
  //             userDetails={userDetails}
  //             handleSubmit={handleSubmit}
  //             // moreFieldsHandleSubmit={moreFieldsHandleSubmit}
  //             activeStep={activeStep}
  //             {...props}
  //           />
  //         </>
  //       )}
  //     </Formik>
  //   );
  // }
  const emergencyExtraFields = [];
  const [emrgencyExtFields, setExtEmergencyFields] = useState([]);
  function Step5(props) {
    const {
      emergencyfields,
      setEmergencyFields,
      emergencyFieldCount,
      setEmergencyFieldCount,
    } = useContext(ApplicationContext);
    const emergencyRef = useRef();
    const handleInputChange = (fieldName, value) => {
      setEmergencyFields({ ...emergencyfields, [fieldName]: value });
    };
    const handlePlusButtonClick = (event) => {
      event?.preventDefault();
      let newFields = {};
      newFields = { ...emergencyfields, ...newFields };
      const newEmergencyFieldCount = emergencyFieldCount + 1;

      newFields[`emergencyName_${newEmergencyFieldCount}`] = "";
      newFields[`emergencyPhone_${newEmergencyFieldCount}`] = "";
      newFields[`emergencyRelation_${newEmergencyFieldCount}`] = "";

      setEmergencyFields(newFields);
      setEmergencyFieldCount(emergencyFieldCount + 1);
    };
    const handleDeleteButtonClick = () => {
      const newFields = { ...emergencyfields };
      for (let i = 0; i < 4; i++) {
        const suffix = emergencyFieldCount - i;
        delete newFields[`emergencyName_${suffix}`];
        delete newFields[`emergencyPhone_${suffix}`];
        delete newFields[`emergencyRelation_${suffix}`];
      }
      setEmergencyFields(newFields);
      setEmergencyFieldCount(emergencyFieldCount - 1);
    };
    const moreFieldsHandleSubmit = (event) => {
      if (emergencyFieldCount == 0) {
        return;
      } else {
        if (isObjectEmpty(emergencyfields) == false) {
          emergencyExtraFields.push(emergencyfields);
        }
      }
      setExtEmergencyFields(emergencyExtraFields);
      return emergencyExtraFields;
    };

    return (
      <Formik
        initialValues={{
          emergencyName: userDetails.emergencyName,
          emergencyPhone: userDetails.emergencyPhone,
          emergencyRelation: userDetails.emergencyRelation,
        }}
        validateOnMount={true}
        onSubmit={(values) => {
          let extra_fields = moreFieldsHandleSubmit() || [];
          if (Array.isArray(extra_fields)) {
            extra_fields = extra_fields?.map((obj) => {
              let temp = {};
              Object.keys(obj).map((key) => {
                if (key.includes("name_")) {
                  temp["name"] = obj[key];
                }
                if (key.includes("phone")) {
                  temp["phone"] = "+" + obj[key];
                }
                if (key.includes("relation")) {
                  temp["relationship"] = obj[key];
                }
              });
              return temp;
            });
          }

          let emergencyContacts = [
            {
              name: values.emergencyName,
              phone: values.emergencyPhone,
              relationship: values.emergencyRelation,
            },
            ...extra_fields,
          ];
          localStorage.setItem(
            "emergencyContacts",
            JSON.stringify(emergencyContacts),
          );
          setUserDetails((data) => ({
            ...data,
            emergencyName: values.emergencyName,
            emergencyPhone: values.emergencyPhone,
            emergencyRelation: values.emergencyRelation,
          }));
          if (!isCompanyLogin) {
            props.nextStep();
          } else {
            setOrderNowBtnLoader(true);
            performSignUp(userDetails);
          }
        }}
        validationSchema={signUpStep4Schema}
        enableReinitialize
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          values,
          setFieldValue,
        }) => (
          <>
            <div className="scroll">
              <div>
                <div className="insurance-heading">
                  <span>{"Contacts to be used in an emergency"}</span>
                </div>

                <Grid container spacing={0.7}>
                  <Grid item xs={12} xl={12} lg={12} md={12}>
                    <div className="mb-1">
                      <InputField
                        name={"emergencyName"}
                        id={"emergencyName"}
                        error={touched.emergencyName && errors.emergencyName}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        fullWidth
                        placeholder={"Name"}
                        value={values.emergencyName}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} xl={12} lg={12} md={12}>
                    <div className="mb-1">
                      <PhoneInputField
                        name={"emergencyPhone"}
                        id={"emergencyPhone"}
                        error={touched.emergencyPhone && errors.emergencyPhone}
                        placeholder={"Phone"}
                        // country={"us"}
                        value={values.emergencyPhone}
                        handleBlur={handleBlur("emergencyPhone")}
                        handleChange={handleChange("emergencyPhone")}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} xl={12} lg={12} md={12}>
                    <div className="mb-1">
                      <Box sx={{ minWidth: 120 }}>
                        <FormControl fullWidth>
                          <InputLabel id="demo-simple-select-label">
                            Relationship
                          </InputLabel>
                          <Select
                            sx={{
                              boxShadow: "none",
                              ".MuiOutlinedInput-notchedOutline": { border: 0 },
                            }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={values.emergencyRelation}
                            label="Relationship"
                            placeholder="Relationship"
                            onChange={handleChange("emergencyRelation")}
                          >
                            {emergencyRelationshipList.map(
                              ({ value, label }) => {
                                return (
                                  <MenuItem value={value}>{label}</MenuItem>
                                );
                              },
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                      <div className="error-input-container">
                        {errors.emergencyRelation &&
                        touched.emergencyRelation ? (
                          <p className="form-error">
                            {errors.emergencyRelation}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>
              <EmergencyInputField
                fields={emergencyfields}
                fieldCount={emergencyFieldCount}
                handleInputChange={handleInputChange}
                handleSubmit={moreFieldsHandleSubmit}
                handleDeleteButtonClick={handleDeleteButtonClick}
                emergencyRef={emergencyRef}
              />
              <div
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <div style={{ width: "30%" }}>
                  <GenericButton
                    disable={emergencyFieldCount == 2}
                    buttonText={"+ Add Contact"}
                    customStyle={"add-more-fields-btn"}
                    onPress={() => {
                      handlePlusButtonClick();
                      emergencyRef.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            <ActionButtons
              setUserDetails={setUserDetails}
              userDetails={userDetails}
              handleSubmit={() => {
                handleSubmit();
                performSignUp(userDetails);
              }}
              activeStep={activeStep}
              loading={orderNowBtnLoader}
              {...props}
            />
          </>
        )}
      </Formik>
    );
  }

  function Step7(props) {
    const [emailVerified, setEmailVerified] = useState(false);
    const [sendingCode, setSendingCode] = useState(true);
    const [sendError, setSendError] = useState("");
    const [otpValue, setOtpValue] = useState("");
    const [otpError, setOtpError] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [currentUid, setCurrentUid] = useState(null);
    const emailSentRef = useRef(false);

    useEffect(() => {
      if (!props.isActive) return;

      const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user && !emailSentRef.current) {
          emailSentRef.current = true;
          setCurrentUid(user.uid);
          try {
            setSendingCode(true);
            await generateAndSendOtpCode(user.uid, userDetails.email || user.email);
            setSendError("");
          } catch (err) {
            setSendError("Failed to send verification email. Use Resend below.");
          } finally {
            setSendingCode(false);
          }
        }
      });

      return () => {
        unsubscribeAuth();
      };
    }, [props.isActive]);

    const handleVerify = async () => {
      if (!otpValue || otpValue.length !== 6) {
        setOtpError("Please enter the 6-digit code from your email.");
        return;
      }
      setVerifying(true);
      setOtpError("");
      try {
        const result = await verifyOtpCode(currentUid, otpValue);
        if (result.success) {
          setEmailVerified(true);
        } else {
          setOtpError(result.message || "Invalid code. Please try again.");
        }
      } catch (err) {
        setOtpError("Something went wrong. Please try again.");
      } finally {
        setVerifying(false);
      }
    };

    const handleResend = async () => {
      if (!currentUid) return;
      try {
        setSendingCode(true);
        setSendError("");
        setOtpValue("");
        setOtpError("");
        await generateAndSendOtpCode(currentUid, userDetails.email, true);
        setResendModalOpen(true);
      } catch (err) {
        setSendError("Failed to resend. Please try again.");
      } finally {
        setSendingCode(false);
      }
    };

    return (
      <div className="step1-container">
        <h2 className="step1-heading">Verify Your Email Address</h2>
        <p className="step1-subheading">
          We’ve sent a 6-digit code to{" "}
          <strong>{userDetails.email}</strong>. Enter it below to continue.
        </p>

        <div className="step1-status">
          {sendingCode ? (
            <div className="loading-indicator">
              <CircularProgress color="success" size={24} />
              <p>Sending verification code...</p>
            </div>
          ) : emailVerified ? (
            <p className="status-message verified">
              Your email is verified! ✅
            </p>
          ) : (
            <div className="otp-input-container">
              {sendError && (
                <p className="status-message not-verified">{sendError}</p>
              )}
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={(e) => {
                  setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setOtpError("");
                }}
                placeholder="Enter 6-digit code"
                className="otp-input"
              />
              {otpError && <p className="form-error">{otpError}</p>}
              <button
                className="otp-verify-btn"
                onClick={handleVerify}
                disabled={verifying || otpValue.length !== 6}
              >
                {verifying ? (
                  <>
                    <CircularProgress color="inherit" size={16} />
                    {" "}Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
            </div>
          )}
        </div>

        <div className="button-container">
          <p className="resend-button" onClick={handleResend}>
            Resend Code
          </p>
        </div>

        <div className="action-buttons">
          <ActionButtons
            setUserDetails={setUserDetails}
            userDetails={userDetails}
            disableNext={!emailVerified}
            handleSubmit={() => {
              if (!emailVerified) return;
              performSignUp(userDetails);
              if (!isCompanyLogin) {
                props.nextStep();
              }
            }}
            {...props}
          />
        </div>

        <GenericModal
          show={resendModalOpen}
          title="A new verification code has been sent to your email!"
          handleClose={() => setResendModalOpen(false)}
          buttonText={constants.ok}
          type={5}
        />
      </div>
    );
  }

  // Extra step (only when insurance is on): collects name + DOB so the Plus
  // plans can be priced by age. Saves the exact same fields/format as before.
  function StepDetails(props) {
    // Track the three DOB parts independently so a single dropdown selection
    // persists (the combined Formik `dob` is only built once all three are set).
    const [dobParts, setDobParts] = useState(() => {
      const v = userDetails.birthDate;
      if (v) {
        const d = new Date(v);
        if (!isNaN(d.getTime())) {
          return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
        }
      }
      return { day: "", month: "", year: "" };
    });
    const nowYear = new Date().getFullYear();
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
    const yearOptions = [];
    for (let y = nowYear - 18; y >= nowYear - 100; y--) yearOptions.push(y);
    return (
      <Formik
        initialValues={{
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          dob: userDetails.birthDate || null,
        }}
        validateOnMount={true}
        enableReinitialize={true}
        onSubmit={(values) => {
          setUserDetails((data) => ({
            ...data,
            firstName: values.firstName,
            lastName: values.lastName,
            birthDate: converDateFormate(values.dob),
          }));
          props.nextStep();
        }}
        validationSchema={signUpDetailsSchema}
      >
        {({ handleChange, handleBlur, handleSubmit, touched, errors, values, setFieldValue, setFieldTouched }) => {
          const onPart = (key, val) => {
            const next = { ...dobParts, [key]: val };
            setDobParts(next);
            const dd = Number(next.day), mm = Number(next.month), yy = Number(next.year);
            // Don't mark the field touched mid-entry — the "select your DOB"
            // error should only appear after the user tries to continue.
            setFieldValue("dob", dd && mm && yy ? new Date(yy, mm - 1, dd) : null);
          };
          return (
          <div className="sign-up-step-cont">
            <div>
              <div className="sign-up-form-heading">
                <span>{"Your details"}</span>
              </div>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MaterialInputField
                    name={"firstName"}
                    id={"firstName"}
                    placeholder={"First Name"}
                    label={"First Name"}
                    error={touched.firstName && errors.firstName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    value={values.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MaterialInputField
                    name={"lastName"}
                    id={"lastName"}
                    placeholder={"Last Name"}
                    label={"Last Name"}
                    error={touched.lastName && errors.lastName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    value={values.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className="dob-field">
                    <label className="dob-label">Date of birth *</label>
                    <div className="dob-selects">
                      <select
                        className="dob-select"
                        value={dobParts.day}
                        onChange={(e) => onPart("day", e.target.value)}
                      >
                        <option value="">Day</option>
                        {dayOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <select
                        className="dob-select dob-select-month"
                        value={dobParts.month}
                        onChange={(e) => onPart("month", e.target.value)}
                      >
                        <option value="">Month</option>
                        {monthNames.map((name, i) => (
                          <option key={i + 1} value={i + 1}>{name}</option>
                        ))}
                      </select>
                      <select
                        className="dob-select"
                        value={dobParts.year}
                        onChange={(e) => onPart("year", e.target.value)}
                      >
                        <option value="">Year</option>
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div className="error-input-container">
                      {errors.dob && touched.dob ? (
                        <p className="form-error">{errors.dob}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <ActionButtons
              setUserDetails={setUserDetails}
              userDetails={userDetails}
              handleSubmit={handleSubmit}
              activeStep={activeStep}
              {...props}
            />
          </div>
          );
        }}
      </Formik>
    );
  }

  function Step6(props) {
    const curCode = (userDetails.currency || "eur").toLowerCase();
    const indivPlus = getPlusPrice("individualPlus", userDetails.birthDate, curCode);
    const corpPlus = getPlusPrice("corporatePlus", userDetails.birthDate, curCode);
    const curSym = { eur: "€", gbp: "£", dkk: "kr", sek: "kr", nok: "kr", usd: "$" }[curCode] || "€";
    const memberAge = getAgeFromDob(userDetails.birthDate);
    const over80 = insuranceEnabled && memberAge != null && memberAge > 80;
    // Plus prices are defined in EUR, GBP, DKK, SEK, NOK and USD. Only offer Plus
    // when we have a price in the member's currency (getPlusPrice returns null
    // otherwise), so a premium is never charged in the wrong currency.
    const insCurrencyOk = PLUS_CURRENCIES.includes(curCode);
    const showIndivPlus = insuranceEnabled && insCurrencyOk && indivPlus != null;
    const showCorpPlus = insuranceEnabled && insCurrencyOk && corpPlus != null;
    return (
      <>
        {over80 && (
          <p className="insurance-age-note">
            Travel insurance isn't available above age 80 — the Plus plans aren't shown, but you can still join with the standard plans below.
          </p>
        )}

        <div className="plans-grid">
          {/* Individual */}
          <div className="plan-card">
            <span className="plan-badge popular">Popular</span>
            <h4 className="plan-name">Individual</h4>
            <div className="plan-price">
              <span className="cur">{curSym}</span>
              <span className="amt">{userDetails.price}</span>
              <span className="per">/year</span>
            </div>
            <ul className="plan-features">
              <li><span className="tick">✓</span>Instantly locate hospitals &amp; pharmacies</li>
              <li><span className="tick">✓</span>Translate allergies, medicine, diagnoses &amp; vaccines into 135 languages</li>
              <li><span className="tick">✓</span>Store health documents &amp; prescriptions</li>
              <li><span className="tick">✓</span>Add insurance information</li>
              <li><span className="tick">✓</span>Share your health information</li>
              <li><span className="tick">✓</span>WMC Emergency Card included</li>
            </ul>
            <div className="plan-cta">
              <GenericButton
                loading={orderNowBtnLoader}
                onPress={() => {
                  setOrderNowBtnLoader(true);
                  handleCreateStripeCard();
                }}
                buttonText={"Order Now"}
                customStyle={"order-now-button"}
              />
            </div>
          </div>

          {showIndivPlus && (
            <div className="plan-card featured">
              <span className="plan-badge insured">Includes insurance</span>
              <h4 className="plan-name">Individual Plus</h4>
              <div className="plan-price">
                <span className="cur">{curSym}</span>
                <span className="amt">{indivPlus}</span>
                <span className="per">/year</span>
              </div>
              <p className="plan-desc">Everything in Individual, plus travel insurance:</p>
              <ul className="plan-features">
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Emergency medical &amp; hospital cover abroad — up to €500,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span>Emergency evacuation &amp; repatriation — up to €100,000</li>
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Luggage &amp; personal effects — up to €2,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Personal liability — up to €500,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span>24/7 emergency assistance</li>
              </ul>
              <p className="plan-conditions">{INSURANCE_CONDITIONS_NOTE}</p>
              <div className="plan-cta">
                <GenericButton
                  loading={orderNowBtnLoader}
                  buttonText={"Order Now"}
                  customStyle={"order-now-button"}
                  onPress={() => {
                    setOrderNowBtnLoader(true);
                    setUserDetails((d) => ({ ...d, plan: "individualPlus", price: indivPlus }));
                    // Remember the insurance purchase across the Stripe redirect
                    // so we can create the policy record once payment is confirmed.
                    localStorage.setItem(
                      "pendingInsurance",
                      JSON.stringify({
                        plan: "individualPlus",
                        product: "Product E",
                        premium: indivPlus,
                        currency: userDetails.currency,
                      }),
                    );
                    handleCreateStripeCard({
                      amount: indivPlus,
                      productName: "Individual Plus (with insurance)",
                      planKey: "individualPlus",
                    });
                  }}
                />
              </div>
            </div>
          )}

          {/* Corporate */}
          <div className="plan-card">
            <span className="plan-badge neutral">Custom</span>
            <h4 className="plan-name">Corporate</h4>
            <div className="plan-price">
              <span className="cur">{curSym}</span>
              <span className="amt">{userDetails.corporatePrice}</span>
              <span className="per">/year</span>
            </div>
            <p className="plan-desc">Contact us for a customised offer including customer support and co-branding.</p>
            <div className="plan-cta">
              <GenericButton
                buttonText={"Contact"}
                customStyle={"contact-now-button"}
                onPress={() => {
                  window.open(
                    `mailto:Contact@wmc-card.no?subject=Request For World Medical Corporate Card &body=First Name: ${userDetails.firstName} Last Name: ${userDetails.lastName} Email: ${userDetails.email} Date Of Birth: ${userDetails.birthDate}`,
                  );
                }}
              />
            </div>
          </div>

          {showCorpPlus && (
            <div className="plan-card featured">
              <span className="plan-badge insured">Includes insurance</span>
              <h4 className="plan-name">Corporate Plus</h4>
              <div className="plan-price">
                <span className="cur">{curSym}</span>
                <span className="amt">{corpPlus}</span>
                <span className="per">/year</span>
              </div>
              <p className="plan-desc">Corporate cover with travel insurance included, per person.</p>
              <ul className="plan-features">
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Emergency medical &amp; hospital cover abroad — up to €500,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span>Emergency evacuation &amp; repatriation — up to €100,000</li>
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Luggage &amp; personal effects — up to €2,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span><span className="feat"><span className="feat-label">Personal liability — up to €500,000</span><span className="plan-excess">€100 deductible</span></span></li>
                <li><span className="tick">✓</span>24/7 emergency assistance</li>
              </ul>
              <p className="plan-conditions">{INSURANCE_CONDITIONS_NOTE}</p>
              <div className="plan-cta">
                <GenericButton
                  buttonText={"Contact"}
                  customStyle={"contact-now-button"}
                  onPress={() => {
                    window.open(
                      `mailto:Contact@wmc-card.no?subject=Request For World Medical Corporate Plus Card&body=First Name: ${userDetails.firstName} Last Name: ${userDetails.lastName} Email: ${userDetails.email} Date Of Birth: ${userDetails.birthDate} Phone: ${userDetails.phone}`,
                    );
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {showIndivPlus && (
          <div className="insurance-checkout-notice insurance-notice-bottom">
            <p className="insurance-notice-text">
              The <strong>Plus</strong> plans include a travel medical insurance
              product administered by <strong>battleface Underwriting Services SRL</strong>. By purchasing a Plus plan you
              confirm that you have read, understood and agree to the policy
              wording and the insurance disclosures.
            </p>
            <p className="insurance-notice-conditions">{INSURANCE_CONDITIONS_NOTE}</p>
            <p className="insurance-notice-conditions">{INSURANCE_COOLING_OFF_NOTE}</p>
            <div className="insurance-doc-links">
              {POLICY_URL ? (
                <a href={POLICY_URL} target="_blank" rel="noopener noreferrer">Policy wording</a>
              ) : null}
              <a href={DISCLOSURES_URL} target="_blank" rel="noopener noreferrer">Insurance disclosures</a>
            </div>
            <p className="insurance-notice-conditions">{INSURANCE_TRUST_NOTE}</p>
            <p className="insurance-notice-conditions">{INSURANCE_PLAN_SUMMARY_NOTE}</p>
            <p className="insurance-notice-conditions">{INSURANCE_PRIVACY_NOTE}</p>
            <p className="insurance-notice-conditions">{INSURANCE_COMPLAINTS_NOTE}</p>
            <p className="insurance-legal-footer">{LEGAL_FOOTER}</p>
          </div>
        )}

        <ActionButtons
          setUserDetails={setUserDetails}
          userDetails={userDetails}
          activeStep={activeStep}
          handleSubmit={() => console.log("")}
          {...props}
        />
      </>
    );
  }
  // UPDATED STEPS - Removed step2, step3, step5 (moved to post-login modals)
  const steps = isCompanyLogin
    ? [step1]
    : insuranceEnabled
      ? [step1, "Your details", step7, step6]
      : [step1, step7, step6];

  const CreateCardStripe = async (uid, opts = {}) => {
    // let selectedCurr = "EUR";
    let selectedCurr = userDetails.currency || "eur";
    // opts.amount (major units) lets a specific plan (e.g. Individual Plus) set
    // its own price; otherwise fall back to the plan price already in state.
    const priceMajor = opts.amount != null ? opts.amount : userDetails.price;
    let amount = priceMajor
      ? Math.round(priceMajor * 100).toString()
      : "1995";
    try {
      // const result = await fetch("https://open.er-api.com/v6/latest/EUR");
      // const currencies = await result.json();
      // const myCurrency = await fetch("https://ipapi.co/currency");
      // const code = await myCurrency.text();
      // selectedCurr = code;
      // amount = Math.ceil(currencies.rates[code]*1178);
      // amount = Math.ceil(currencies.rates[code] * 995);
      // below one was uncommented
      // amount = Math.ceil(currencies.rates[code] * 1995);
    } catch (err) {
      selectedCurr = BASE_CURRENCY.toLowerCase();
      // amount = 1178.00;
      // amount = 995.0;
      amount = "1995";
    }
    try {
      // Use server-side Netlify Function instead of client-side Stripe SDK
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: window.location.hostname.includes('.co.uk') ? 'uk' : 'io',
          currency: window.location.hostname.includes('.co.uk') ? 'gbp' : selectedCurr,
          productName: opts.productName || 'New Private Plan',
          unitAmount: opts.amount != null
            ? amount
            : (window.location.hostname.includes('.co.uk') ? '2400' : amount),
          interval: 'year',
          intervalCount: 1,
          // Carry the plan in the return URL too, so the insurance policy can be
          // reconstructed even if localStorage was cleared across the redirect
          // (e.g. Safari ITP, or returning in a different tab).
          successUrl: `${window.location.origin}/sign-up?session_id=${uid}&payment={CHECKOUT_SESSION_ID}${opts.planKey ? `&plan=${encodeURIComponent(opts.planKey)}&premium=${encodeURIComponent(opts.amount != null ? opts.amount : "")}&cur=${encodeURIComponent(userDetails.currency || "")}` : ""}`,
          cancelUrl: `${window.location.origin}/setup/subscription`,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        console.error('Checkout session error:', data?.error);
        setShowModal(true);
        setApiResponse({
          type: ENUMS.MODAL_TYPES.ERROR,
          title: "Payment Error",
          body: data?.error || "Failed to create checkout session. Please try again.",
        });
        setOrderNowBtnLoader(false);
        return;
      }
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        setOrderNowBtnLoader(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err.message);
      setShowModal(true);
      setApiResponse({
        type: ENUMS.MODAL_TYPES.ERROR,
        title: "Payment Error",
        body: "Failed to create checkout session. Please try again.",
      });
      setOrderNowBtnLoader(false);
    }
  };

  const handleCreateStripeCard = async (opts = {}) => {
    const uid = auth.currentUser.uid;
    if (!isCompanyLogin) {
      CreateCardStripe(uid, opts);
    } else {
      const getData = await getTempUserDetail();
      const url = `${constants.siteBaseURL}/${company}/sign-up?session_id=${uid}`;
      window.location.assign(url);
    }
  };

  const performSignUp = async (payload) => {
    try {
      // Idempotent: the account is created before email verification, and later
      // steps call this again with the same email. Don't re-create it — Firebase
      // rejects a duplicate as "email already in use" and the user (already
      // signed in as this new account) sees a false "Email already exists" error.
      // If we're already signed in as this email, just refresh temp data and move on.
      const alreadyOurAccount =
        auth.currentUser &&
        (auth.currentUser.email || "").toLowerCase() ===
          (payload?.email || "").toLowerCase();
      if (alreadyOurAccount) {
        const item = transformUserData(userDetails);
        setTempUserDetail({ uid: auth.currentUser.uid, item });
        if (isCompanyLogin) saveDataToFirebase(auth.currentUser.uid);
        setOrderNowBtnLoader(false);
        return;
      }
      let response = await performSignUpThroughFirebase(payload);
      if (response) {
        const {
          user: { accessToken, uid, reloadUserInfo },
        } = response;
        const item = transformUserData(userDetails);
        setTempUserDetail({
          uid,
          item,
        });
        if (isCompanyLogin) {
          saveDataToFirebase(uid);
        }
      }

      setOrderNowBtnLoader(false);
    } catch (error) {
      const isEmailInUse =
        error?.code === "auth/email-already-in-use" ||
        /already.*in.?use|already exists/i.test(error?.message || "");
      setShowModal(true);
      setApiResponse({
        type: ENUMS.MODAL_TYPES.ERROR,
        title: "Error",
        body: isEmailInUse
          ? "Email already exists."
          : "Something went wrong creating your account. Please try again.",
      });
      console.log("this error 1", error);
      setOrderNowBtnLoader(false);
    }
  };
  const saveDataToFirebase = async (id, payment) => {
    let getData = await getTempUserDetail();
    if (getData && getData.item) {
      getData = {
        item: {
          ...getData.item,
          paymentId: isCompanyLogin ? null : payment,
        },
      };
      // After the Stripe redirect, make sure Firebase has restored the session
      // before writing user/{uid} — otherwise the write races auth and is
      // denied, which used to leave the loading screen hanging forever.
      try { await auth.authStateReady(); } catch (e) { /* older SDKs: ignore */ }
      let response = await saveUserData(id, getData?.item);
      // saveUserData returns undefined on success, error string on failure
      if (typeof response !== 'string') {
        // Trigger PDF card generation via Cloud Function
        // Fire-and-forget: the PDF service must never block (or hang) account
        // creation. It runs in the background; the user proceeds immediately.
        fetch(
          `https://europe-west1-world-medical-card-prod.cloudfunctions.net/wmc-pdf-generator?userId=${id}`
        )
          .then(() => console.log('[Sign-up] PDF generation triggered for user:', id))
          .catch((pdfErr) => console.error('[Sign-up] PDF generation failed:', pdfErr.message));
        // Fire AWIN conversion tracking on successful payment
        if (!isCompanyLogin && payment) {
          fireAwinConversion({
            orderRef: payment,
            orderSubtotal: userDetails.price,
            currencyCode: userDetails.currency,
            commissionGroup: "Sale",
            saleAmount: userDetails.price,
            voucherCode: "",
            customerAcquisition: "NEW",
          });
        }
        // If the user purchased travel insurance, record the policy so their
        // cover shows in the app. (Notifying battleface via their order API is a
        // separate step that still needs their Create Order endpoint spec.)
        let boughtInsurance = false;
        try {
          // Prefer localStorage, but fall back to the plan/premium/currency
          // carried in the RETURN URL if storage didn't survive the Stripe
          // redirect (private browsing, cleared storage, a different browser).
          // Carrying all three in the URL means the policy is still recorded
          // even when localStorage is gone — otherwise the customer is charged
          // with no policy created.
          let pending = localStorage.getItem("pendingInsurance");
          if (!pending && !isCompanyLogin && payment) {
            const planFromUrl = searchParams?.get("plan");
            if (planFromUrl) {
              const premiumFromUrl = searchParams?.get("premium");
              const curFromUrl = searchParams?.get("cur");
              pending = JSON.stringify({
                plan: planFromUrl,
                product: "Product E",
                premium: premiumFromUrl
                  ? Number(premiumFromUrl)
                  : getPlusPrice(planFromUrl, userDetails.birthDate, curFromUrl || userDetails.currency),
                currency: curFromUrl || userDetails.currency,
              });
            }
          }
          if (!isCompanyLogin && payment && pending) {
            const info = JSON.parse(pending);
            const start = new Date();
            const end = new Date();
            end.setFullYear(end.getFullYear() + 1);
            const fmt = (d) =>
              `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            const coverStart = fmt(start);
            const coverEnd = fmt(end);
            const iso = (d) => d.toISOString().slice(0, 10);
            const toIsoDob = (s) => {
              if (!s) return s;
              const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              if (m) return `${m[3]}-${m[2]}-${m[1]}`;
              const d = new Date(s);
              return isNaN(d) ? s : d.toISOString().slice(0, 10);
            };
            const withTimeout = (p, ms = 15000) =>
              Promise.race([
                p,
                new Promise((_, rej) => setTimeout(() => rej(new Error("battleface timeout")), ms)),
              ]);

            // battleface order: create the authoritative insurance order
            // (Quote -> Order -> Process Payment). Non-blocking: the customer
            // has already paid, so if battleface is unreachable or rejects the
            // request we still save a local policy (WMC-E fallback number) and
            // record the battleface response under battlefaceError for review.
            // Field mappings in netlify/lib/battleface.js are still being
            // confirmed against the battleface Postman spec; a rejection here
            // surfaces their exact response in the console and on the record.
            let bfOrderId = null;
            let bfPolicyNumber = null;
            let bfDocuments = [];
            let bfError = null;
            try {
              const dobISO = toIsoDob(userDetails.birthDate);
              const bfCurrency = (info.currency || userDetails.currency || "EUR").toUpperCase();
              const hostCountry = toIso2(userDetails.country || userDetails.nationality);
              const stateCode = needsStateCode(hostCountry) ? (userDetails.state || "") : "";
              const quote = await withTimeout(
                insuranceApi.getQuote({
                  startDate: iso(start),
                  endDate: iso(end),
                  currency: bfCurrency,
                  hostCountry,
                  hostCountryState: stateCode,
                  travellers: [{ dob: dobISO, isMain: true }],
                })
              );
              const order = await withTimeout(
                insuranceApi.createOrder({
                  quoteId: quote.quoteId,
                  currency: bfCurrency,
                  startDate: iso(start),
                  endDate: iso(end),
                  depositDate: iso(new Date()),
                  travellers: [
                    {
                      firstName: userDetails.firstName,
                      middleName: "",
                      lastName: userDetails.lastName,
                      dob: dobISO,
                      email: userDetails.email,
                      contactNumber: userDetails.phone || "",
                      address: userDetails.address || "",
                      city: userDetails.city || "",
                      postCode: userDetails.zipCode || "",
                      countryCode: hostCountry,
                      stateCode,
                      isMain: true,
                    },
                  ],
                })
              );
              bfOrderId = order.orderNumber || order.orderId || null;
              bfPolicyNumber = order.policyNumber || null;
              bfDocuments = order.documents || [];
              const approved = await withTimeout(
                insuranceApi.processPayment({
                  orderId: order.orderNumber || order.orderId,
                })
              );
              if (approved) {
                bfPolicyNumber = approved.policyNumber || bfPolicyNumber;
                bfDocuments = approved.documents && approved.documents.length ? approved.documents : bfDocuments;
              }
              console.log("[Sign-up] battleface order created:", bfOrderId, bfPolicyNumber);
            } catch (bfErr) {
              bfError = (bfErr && (bfErr.details || bfErr.message)) || String(bfErr);
              console.error("[Sign-up] battleface order failed:", bfErr && bfErr.message, bfErr && bfErr.details);
            }

            const policyNumber = bfPolicyNumber || `WMC-E-${String(payment).slice(-8).toUpperCase()}`;
            await savePolicy({
              userId: id,
              status: "approved",
              product: info.product || "Product E",
              plan: info.plan || "individualPlus",
              policyNumber,
              orderId: payment,
              battlefaceOrderId: bfOrderId,
              battlefaceError: bfError,
              currency: info.currency || userDetails.currency,
              premium: info.premium,
              trip: {
                startDate: coverStart,
                endDate: coverEnd,
                destinationCountry: "Worldwide (annual multi-trip)",
              },
              documents: bfDocuments,
            });
            localStorage.removeItem("pendingInsurance");
            console.log("[Sign-up] Insurance policy created for user:", id);
            boughtInsurance = true;
            setInsConfirm({
              policyNumber,
              coverStart,
              coverEnd,
              premium: info.premium,
              currency: info.currency || userDetails.currency,
            });
            // Send the insurance confirmation / fulfilment email (best-effort).
            try {
              const symMap = { eur: "€", gbp: "£", dkk: "kr", sek: "kr", nok: "kr", usd: "$" };
              const curL = (info.currency || userDetails.currency || "eur").toLowerCase();
              const sym = symMap[curL] || "€";
              const money = (v) => (v == null || v === "" ? "" : `${sym}${Number(v).toFixed(2)}`);
              const planLabel = info.plan === "corporatePlus" ? "Corporate Plus" : "Individual Plus";
              sendInsuranceConfirmation({
                toEmail: userDetails.email,
                firstName: userDetails.firstName,
                fullName: [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" "),
                planName: planLabel,
                policyNumber,
                purchaseDate: coverStart,
                amountCharged: money(info.premium),
                annualPremium: money(info.premium),
                coverStart,
                coverEnd,
                policyUrl: process.env.REACT_APP_INSURANCE_POLICY_URL || "",
              });
            } catch (mailErr) {
              console.error("[Sign-up] Confirmation email dispatch failed:", mailErr?.message);
            }
          }
        } catch (polErr) {
          console.error("[Sign-up] Failed to create insurance policy:", polErr?.message);
        }
        localStorage.setItem("welcome", "true");
        if (boughtInsurance) {
          // Show the insurance confirmation screen; sign-in happens on Continue.
          setPaymentProcessing(false);
        } else {
          // Auto-login after successful account creation — no button press needed
          await performLogin();
        }
      } else {
        // The write failed (e.g. permission/timing after the Stripe return).
        // Don't leave the spinner hanging — log it, stop the loader, and sign
        // the user in so they can finish in the order wizard (which now creates
        // the profile document itself).
        console.error('[Sign-up] saveUserData failed, recovering:', response);
        setPaymentProcessing(false);
        await performLogin();
      }
    } else {
      // Temp signup data was lost after the Stripe redirect (storage cleared, or
      // the account already existed so performSignUp never stored it). Never leave
      // the spinner hanging: stop the loader and sign the user in so they can
      // finish in the order wizard (which creates the profile document itself).
      console.error("[Sign-up] No temp user data found after payment; recovering via login");
      setPaymentProcessing(false);
      try {
        await performLogin();
      } catch (e) {
        setShowModal(true);
        setApiResponse({
          type: ENUMS.MODAL_TYPES.ERROR,
          title: "Error",
          body: "We couldn't finish setting up your account automatically. Please sign in to continue.",
        });
      }
    }
  };
  const checkEmail = async (payload, props) => {
    const response = await checkIfEmailExists(payload?.email);
    if (response) {
      // Email doesn't exist — fresh signup
      setUserDetails((data) => ({
        ...data,
        email: payload.email,
        password: payload.password,
        confirmPassword: payload.confirmPassword,
      }));
      await performSignUpThroughFirebase({
        email: payload.email,
        password: payload.password,
      });
      props.nextStep();
    } else {
      // Email exists in Firebase Auth — check if they have a Firestore doc
      const existingUserDoc = await getUserByEmail(payload.email);
      if (!existingUserDoc) {
        // Firebase auth user exists but no Firestore doc = incomplete signup
        // Sign them back in and let them resume from verification
        setUserDetails((data) => ({
          ...data,
          email: payload.email,
          password: payload.password,
          confirmPassword: payload.confirmPassword,
        }));
        const result = await performSignUpThroughFirebase({
          email: payload.email,
          password: payload.password,
        });
        if (result && result.user) {
          props.nextStep();
        } else {
          setApiResponse({
            type: ENUMS.MODAL_TYPES.ERROR,
            title: "Error",
            body: typeof result === "string" ? result : "Incorrect password. Please try again.",
          });
          setShowModal(true);
        }
      } else {
        // Fully registered user already exists
        setApiResponse({
          type: ENUMS.MODAL_TYPES.ERROR,
          title: "Error",
          body: "Email already exists.",
        });
        setShowModal(true);
      }
    }
  };
  const performLogin = async () => {
    setBtnLoader(true);
    const getData = await getTempUserDetail();
    if (getData) {
      let payload = {
        email: getData?.item?.email,
        password: getData?.item?.password,
      };
      try {
        const result = await performLoginThroughFirebase(payload);
        if (result) {
          const {
            user: { accessToken, uid, reloadUserInfo },
          } = result;
          setToken(accessToken);
          setProfile({
            uid,
            reloadUserInfo,
          });
          const responseData = await getUserDetails(uid);
          setSecondaryProfile(responseData);
          navigate({ pathname: routePaths.home });
        }
        setBtnLoader(false);
        setShowModal(false);
      } catch (e) {
        setShowModal(true);
        setApiResponse({
          type: ENUMS.MODAL_TYPES.ERROR,
          title: "Error",
          body: e.message,
        });
        setBtnLoader(false);
        console.log("Error : ", e.message);
      }
    }
  };
  // The checkout is always the last step. On it we hide the left card panel and
  // let the plans use the full width, so the pricing fits without scrolling.
  const isCheckoutStep = !isCompanyLogin && activeStep === steps.length - 1;
  return (
    <div className={`signin-container${isCheckoutStep ? " checkout-step" : ""}`}>
      {paymentProcessing && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-card">
            <CircularProgress size={44} style={{ color: "#47b6f6" }} />
            <h3>Setting up your account…</h3>
            <p>We're confirming your payment and creating your World Medical Card. This only takes a moment.</p>
          </div>
        </div>
      )}
      {insConfirm && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-card insurance-confirm-card">
            <div className="insurance-confirm-check">✓</div>
            <h3>You're covered</h3>
            <p>Your World Medical Card includes travel insurance. Here are your policy details:</p>
            <div className="insurance-confirm-summary">
              <div className="ics-row"><span>Policy number</span><strong>{insConfirm.policyNumber}</strong></div>
              <div className="ics-row"><span>Cover period</span><strong>{insConfirm.coverStart} – {insConfirm.coverEnd}</strong></div>
              <div className="ics-row"><span>Premium</span><strong>{({ eur: "€", gbp: "£", dkk: "kr", sek: "kr", nok: "kr", usd: "$" }[insConfirm.currency] || "€")}{insConfirm.premium}/year</strong></div>
              <div className="ics-row"><span>Cover area</span><strong>Worldwide · annual multi-trip</strong></div>
            </div>
            <p className="ics-conditions">{INSURANCE_CONDITIONS_NOTE}</p>
            <p className="ics-renew-note">Your cover renews automatically on {insConfirm.coverEnd} at the then-current rate for your age band.</p>
            <p className="ics-email-note">We've sent your policy documents to <strong>{userDetails.email}</strong>.</p>
            <div className="insurance-doc-links insurance-confirm-links">
              {POLICY_URL ? (<a href={POLICY_URL} target="_blank" rel="noopener noreferrer">Policy wording</a>) : null}
              <a href={DISCLOSURES_URL} target="_blank" rel="noopener noreferrer">Insurance disclosures</a>
            </div>
            <p className="insurance-legal-footer">{LEGAL_FOOTER}</p>
            <GenericButton
              buttonText={"Continue to my account"}
              customStyle={"order-now-button"}
              onPress={async () => { setInsConfirm(null); await performLogin(); }}
            />
          </div>
        </div>
      )}
      {!isCompanyLogin && <Navigation />}
      {!isCheckoutStep && (
      <div className="leftPlan" id="leftPlan">
        <div className="leftPlanInner" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="mobile-logo-cont">
            {isCompanyLogin && (
              <div className="wmcImagBox">
                <img
                  src={wmcLogo}
                  height="auto"
                  width="100%"
                  alt="no-image"
                  style={{ borderRadius: 0 }}
                />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', flex: 1, width: '100%', paddingBottom: '8px' }}>
            {isCompanyLogin && (
              <p style={{ fontSize: '2.3rem', fontWeight: 700, lineHeight: 1.3, margin: '0 0 20px', color: 'white', textAlign: 'center', opacity: 0.95 }}>
                Welcome to your free World Medical Card membership.
              </p>
            )}
            {company === 'usa' && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 64, lineHeight: 1 }}>🇺🇸</span>
              </div>
            )}
            {company === 'fav-winner' && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <img
                  src={images.favWinnerLogo}
                  alt="FAV 50 år"
                  style={{ height: 120, width: 'auto', maxWidth: '100%' }}
                />
              </div>
            )}
            <div className="mobileImageBox" style={{ width: '100%' }}>
            <MedicalCard {...userDetails} />
            {/* <img src={mobileLogo} height="auto" width="100%" /> */}
            {/* <div>
              <h1 className="msg">{getOurApp}</h1>
              <div className="store-logo-cont">
                <a href={constants.appStoreAppLink}>
                  <img src={appStoreLogo} height="auto" width="100%" />
                </a>
                <a href={constants.playStoreAppLink}>
                  <img src={playStoreLogo} height="auto" width="100%" />
                </a>
              </div>
            </div> */}
            </div>
          </div>
        </div>
      </div>
      )}
      <div className={`right-container${isCheckoutStep ? " checkout-full" : ""}`}>
        <div className="wizard-wrap">
          <>
            <div className="wmc-heading-text">
              <h1 className="welcome">{isCheckoutStep ? "Choose your plan" : registerNewWMCAcc}</h1>
              <p className="signInMsg">{isCheckoutStep ? "Select the World Medical Card plan that's right for you to complete your membership." : pleaseFilloutPersonalInfo}</p>
            </div>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <StepWizard onStepChange={handleStepChange}>
              <Step1 />
              {insuranceEnabled && <StepDetails />}
              <Step7 />

              {/* STEPS MOVED TO POST-LOGIN MODALS - COMMENTED OUT */}
              {/* <Step2 /> */}
              {/* <Step3 /> */}
              {/* <Step4 /> */}
              {/* <Step5 /> */}
              {/* <Step7 />   client want verification at 2nd step */}
              {!isCompanyLogin && <Step6 />}
            </StepWizard>
          </>
          <div className="already-have-an-account-cont">
            <Link to="/sign-in">
              <p className="noAccountLink">{alreadyHaveAndAcc}</p>
            </Link>
          </div>
        </div>
      </div>
      <GenericModal
        show={showModal}
        type={apiResponse.type}
        title={apiResponse.title}
        body={apiResponse.body}
        buttonText={constants.ok}
        btnLoader={btnLoader}
        handleClose={() => {
          if (apiResponse.type == ENUMS.MODAL_TYPES.ERROR) {
            setShowModal(false);
          } else {
            searchParams.delete("seesion_id");
            setSearchParams(searchParams);
            performLogin();
          }
        }}
      />
    </div>
  );
};

// ── Access-code gate wrapper ──────────────────────────────────────────────────
// Sits in front of SignUp for specific company landing pages.
// Add an entry here to gate a new /:company/sign-up page behind an access code.
// Removing a company's entry disables the gate for that page (open access).
const COMPANY_ACCESS_CODES = {
  usa: 'USA2026X',
  'fav-winner': 'FAV50',
};

function AccessCodeGate({ code, sessionKey, onUnlock }) {
  const [codeInput,   setCodeInput]   = useState('');
  const [codeError,   setCodeError]   = useState('');
  const [codeVisible, setCodeVisible] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (codeInput.trim() === code) {
      sessionStorage.setItem(sessionKey, '1');
      onUnlock();
    } else {
      setCodeError('Incorrect access code. Please try again.');
      setCodeInput('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F7F8FA', display: 'flex', flexDirection: 'column' }}>
      <Navigation />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            padding: '40px 36px',
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <img src={images.wmcLogo} alt='World Medical Card' style={{ height: 52, marginBottom: 28 }} />

          <h2 style={{ margin: '0 0 8px 0', fontSize: 22, fontWeight: 700, color: '#101828' }}>
            Access required
          </h2>
          <p style={{ margin: '0 0 28px 0', fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>
            Enter your access code to continue to the sign-up page.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <input
                type={codeVisible ? 'text' : 'password'}
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value); setCodeError(''); }}
                placeholder='Access code'
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 52px 12px 16px',
                  fontSize: 15,
                  border: codeError ? '1.5px solid #ef4444' : '1.5px solid #d1d5db',
                  borderRadius: 10,
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  color: '#111827',
                  background: '#fafafa',
                }}
              />
              <button
                type='button'
                onClick={() => setCodeVisible((v) => !v)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', fontSize: 13, padding: 0, fontFamily: 'inherit',
                }}
              >
                {codeVisible ? 'Hide' : 'Show'}
              </button>
            </div>

            {codeError && (
              <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#ef4444', textAlign: 'left' }}>
                {codeError}
              </p>
            )}

            <button
              type='submit'
              disabled={!codeInput.trim()}
              style={{
                width: '100%', padding: '13px',
                background: codeInput.trim() ? '#2563EB' : '#93c5fd',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: codeInput.trim() ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SignUpPage() {
  const { company } = useParams();
  const gateCode = COMPANY_ACCESS_CODES[company];
  const needsGate = !!gateCode;
  const sessionKey = `wmc_${company}_unlocked`;
  const [unlocked, setUnlocked] = useState(
    () => !needsGate || sessionStorage.getItem(sessionKey) === '1'
  );

  if (!unlocked) {
    return (
      <AccessCodeGate
        code={gateCode}
        sessionKey={sessionKey}
        onUnlock={() => setUnlocked(true)}
      />
    );
  }
  return <SignUp />;
}

export default SignUpPage;
